import React, { useState, useMemo, useCallback } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import type { KanbanColumn } from "../../types/pipeline.types";
import type {
  StatusConfig,
  ArchiveReasonConfig,
  ArchiveData,
} from "../StatusControl/types";
import { StatusKanbanColumn } from "./StatusKanbanColumn";
import { ArchiveModal } from "../StatusControl/ArchiveModal";
import "./StatusKanban.css";

interface BoardItem {
  id: number;
  status: string;
}

export interface StatusKanbanBoardProps<T extends BoardItem> {
  items: T[];
  columns: KanbanColumn[];
  statusConfig: Record<string, StatusConfig>;
  hiddenByDefault: string[];
  archiveColumnKey: string;
  archiveReasons: Record<string, ArchiveReasonConfig>;
  entityName: string;
  validTransitions: Record<string, string[]> | null;
  onStatusChange: (
    itemId: number,
    newStatus: string,
    note: string,
  ) => Promise<void>;
  onArchive: (itemId: number, data: ArchiveData) => Promise<void>;
  renderCard: (item: T, isDragOverlay?: boolean) => React.ReactNode;
  isLoading: boolean;
  persistKey: string;
  showDuplicateField?: boolean;
  showGdprWarning?: boolean;
}

export function StatusKanbanBoard<T extends BoardItem>({
  items,
  columns,
  statusConfig,
  hiddenByDefault,
  archiveColumnKey,
  archiveReasons,
  entityName,
  validTransitions,
  onStatusChange,
  onArchive,
  renderCard,
  isLoading,
  persistKey,
  showDuplicateField = false,
  showGdprWarning = false,
}: StatusKanbanBoardProps<T>) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [pendingArchive, setPendingArchive] = useState<{
    itemId: number;
  } | null>(null);
  const [archiveLoading, setArchiveLoading] = useState(false);

  const [showHidden, setShowHidden] = useState(() => {
    try {
      return localStorage.getItem(persistKey) === "true";
    } catch {
      return false;
    }
  });

  const toggleHidden = useCallback(() => {
    setShowHidden((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(persistKey, String(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, [persistKey]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const visibleColumns = useMemo(
    () =>
      showHidden
        ? columns
        : columns.filter((col) => !hiddenByDefault.includes(col.key)),
    [columns, hiddenByDefault, showHidden],
  );

  const itemsByColumn = useMemo(() => {
    const map: Record<string, T[]> = {};
    for (const col of columns) {
      map[col.key] = [];
    }
    for (const item of items) {
      const col = columns.find((c) => c.statuses.includes(item.status));
      if (col) {
        map[col.key].push(item);
      }
    }
    return map;
  }, [items, columns]);

  const hiddenCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const key of hiddenByDefault) {
      counts[key] = itemsByColumn[key]?.length || 0;
    }
    return counts;
  }, [hiddenByDefault, itemsByColumn]);

  const totalHidden = useMemo(
    () => Object.values(hiddenCounts).reduce((sum, c) => sum + c, 0),
    [hiddenCounts],
  );

  const activeItem = useMemo(
    () => (activeId ? items.find((i) => String(i.id) === activeId) : undefined),
    [activeId, items],
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      setActiveId(null);

      const { active, over } = event;
      if (!over) return;

      const draggedItem = items.find((i) => String(i.id) === String(active.id));
      if (!draggedItem) return;

      const overId = String(over.id);
      const statusColPrefix = "status-col-";
      let targetColumn: KanbanColumn | undefined;

      if (overId.startsWith(statusColPrefix)) {
        const colKey = overId.replace(statusColPrefix, "");
        targetColumn = columns.find((col) => col.key === colKey);
      } else {
        const overItem = items.find((i) => String(i.id) === overId);
        if (overItem) {
          targetColumn = columns.find((col) =>
            col.statuses.includes(overItem.status),
          );
        }
      }

      if (!targetColumn) return;
      if (targetColumn.statuses.includes(draggedItem.status)) return;

      // Archive interception
      if (targetColumn.key === archiveColumnKey) {
        setPendingArchive({ itemId: draggedItem.id });
        return;
      }

      // Resolve target status
      let targetStatus: string;
      if (validTransitions) {
        const allowed = validTransitions[draggedItem.status] || [];
        const validTargets = targetColumn.statuses.filter((s) =>
          allowed.includes(s),
        );
        if (validTargets.length === 0) return;
        targetStatus = validTargets[0];
      } else {
        targetStatus = targetColumn.statuses[0];
      }

      await onStatusChange(
        draggedItem.id,
        targetStatus,
        `Moved to ${targetColumn.label} via board`,
      );
    },
    [items, columns, archiveColumnKey, validTransitions, onStatusChange],
  );

  const handleArchiveConfirm = useCallback(
    async (data: ArchiveData) => {
      if (!pendingArchive) return;
      setArchiveLoading(true);
      try {
        await onArchive(pendingArchive.itemId, data);
        setPendingArchive(null);
      } finally {
        setArchiveLoading(false);
      }
    },
    [pendingArchive, onArchive],
  );

  if (isLoading) {
    return (
      <div className="status-kanban-board">
        <div className="status-kanban-columns">
          {columns.slice(0, 5).map((col) => (
            <div
              key={col.key}
              className="status-kanban-column status-kanban-column--skeleton"
            >
              <div
                className="status-kanban-column__header"
                style={{ backgroundColor: "#e5e7eb" }}
              >
                <h3>{col.label}</h3>
                <span className="status-kanban-column__count">-</span>
              </div>
              <div className="status-kanban-column__content">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="status-kanban-card--skeleton" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="status-kanban-board" data-testid="status-kanban-board">
      <div className="status-kanban-toolbar">
        <button
          type="button"
          className={`status-kanban-toggle ${showHidden ? "active" : ""}`}
          onClick={toggleHidden}
          data-testid="toggle-hidden-columns"
        >
          {showHidden ? "Hide" : "Show"} completed
          {!showHidden && totalHidden > 0 && (
            <span className="status-kanban-toggle__badge">{totalHidden}</span>
          )}
        </button>
      </div>

      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="status-kanban-columns">
          {visibleColumns.map((col) => {
            const colItems = itemsByColumn[col.key] || [];
            return (
              <SortableContext
                key={col.key}
                items={colItems.map((i) => String(i.id))}
                strategy={verticalListSortingStrategy}
              >
                <StatusKanbanColumn
                  column={col}
                  statusConfig={statusConfig}
                  itemCount={colItems.length}
                >
                  {colItems.map((item) => renderCard(item, false))}
                </StatusKanbanColumn>
              </SortableContext>
            );
          })}
        </div>

        <DragOverlay>
          {activeItem ? renderCard(activeItem, true) : null}
        </DragOverlay>
      </DndContext>

      <ArchiveModal
        isOpen={pendingArchive !== null}
        onClose={() => setPendingArchive(null)}
        onArchive={handleArchiveConfirm}
        isLoading={archiveLoading}
        entityName={entityName}
        archiveReasons={archiveReasons}
        showDuplicateField={showDuplicateField}
        showGdprWarning={showGdprWarning}
      />
    </div>
  );
}
